import type { UnifiedCanvas } from '../modes/unified/UnifiedCanvas';
import type { ExportOptions } from '../types';
import { showExportModal } from '../ui/modals/exportModal';


export interface ExportContext {
  getUnifiedCanvas: () => UnifiedCanvas | null;
}

export class ExportController {
  private readonly context: ExportContext;

  constructor(context: ExportContext) {
    this.context = context;
  }

  openExportModal(): void {
    const canvas = this.context.getUnifiedCanvas();
    const hasSelection = canvas?.hasSelection() ?? false;

    const onCopyToClipboard = (options: ExportOptions): Promise<boolean> =>
      this.copyToClipboard(options);
    const onExport = (options: ExportOptions): void => this.performExport(options);

    showExportModal({
      hasSelection: Boolean(hasSelection),
      onCopyToClipboard,
      onExport,
    });
  }

  async copyToClipboard(options: ExportOptions): Promise<boolean> {
    const { transparent, scale, selectionOnly } = options;
    const canvas = this.context.getUnifiedCanvas();
    if (!canvas) return false;

    const dataUrl = canvas.getCanvasDataUrl({ transparent, scale, selectionOnly });

    try {
      if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
        return false;
      }

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      return true;
    } catch {
      // Clipboard write can fail due to permissions; ignore for now.
      return false;
    }
  }

  performExport(options: ExportOptions): void {
    const { format, transparent, scale, selectionOnly } = options;
    const canvas = this.context.getUnifiedCanvas();
    if (!canvas) return;

    if (format === 'png') {
      canvas.exportPNG({ transparent, scale, selectionOnly });
    } else {
      canvas.exportSVG({ transparent });
    }
  }
}
